import {IntlShape, MessageDescriptor, defineMessages} from 'react-intl'

/**
 * Etiquetas de los dos menús de configuración del pie del sidebar:
 * **My Agency** (`routesSetting.ts`) y **Policytools Settings**
 * (`routesPolicytoolsSettings.ts`).
 *
 * Los dos consts de rutas se pintan como `{info.name}` / `{info.title}` —
 * variables—, así que el rule no los veía y salían enteros en inglés mientras el
 * sidebar principal ya estaba traducido (plan §10.8, quinto y sexto caso del
 * patrón). Aquí solo viven las etiquetas: `route`, `perm` y `group` siguen en el
 * const, que es quien manda sobre la navegación y los permisos.
 *
 * Namespace `MENU.settings.*`, junto al del sidebar principal
 * (`sidebar-menu/menuMessages.ts`).
 *
 * No se traducen: `PolicyTools` (marca) ni el nombre de la organización, que
 * viene de BD y sustituye a «My Agency» cuando existe.
 */
export const SETTINGS_MENU_MESSAGES = defineMessages({
  // ── Cabeceras de los dos menús ──
  myAgency: {id: 'MENU.settings.myAgency', defaultMessage: 'My Agency'},
  policytoolsSettings: {
    id: 'MENU.settings.policytoolsSettings',
    defaultMessage: 'Policytools Settings',
  },
  /**
   * Cabeceras de columna del menú de escritorio. Van en mayúsculas en inglés
   * porque así está el diseño; en español se escriben normales (guía §17.6).
   */
  headerManage: {id: 'MENU.settings.header.manage', defaultMessage: 'MANAGE'},
  headerMyAgency: {id: 'MENU.settings.header.myAgency', defaultMessage: 'MY AGENCY'},

  // ── Títulos de sección de `routesSetting.ts` ──
  sectionManage: {id: 'MENU.settings.section.manage', defaultMessage: 'Manage'},
  sectionMyAgency: {id: 'MENU.settings.section.myAgency', defaultMessage: 'My Agency'},
  sectionCompensation: {
    id: 'MENU.settings.section.compensation',
    defaultMessage: 'Compensation',
  },
  sectionRetention: {
    id: 'MENU.settings.section.retention',
    defaultMessage: 'Customer retention',
  },
})

type Labels = Record<string, MessageDescriptor>

/**
 * Etiqueta de cada entrada de `ROUTES_SETTINGS`, indexada por su `route`. Se usa
 * la ruta y no el `id` porque el `id` es un número suelto que se reordena al
 * añadir entradas; la ruta es lo que identifica de verdad al destino.
 */
const ROUTE_LABEL: Labels = defineMessages({
  '/integration': {id: 'MENU.settings.integration', defaultMessage: 'Integration'},
  '/pipelines': {id: 'MENU.settings.pipelines', defaultMessage: 'Pipelines'},
  '/assignment-groups': {
    id: 'MENU.settings.assignmentGroups',
    defaultMessage: 'Assignment Groups',
  },
  '/carriers': {id: 'MENU.settings.carriers', defaultMessage: 'Carriers'},
  '/lead-sources': {id: 'MENU.settings.leadSources', defaultMessage: 'Lead Sources'},
  '/policy-statuses': {id: 'MENU.settings.policyStatuses', defaultMessage: 'Policy Statuses'},
  '/states-eu': {id: 'MENU.settings.usStates', defaultMessage: 'US States'},
  '/lead-loss-reasons': {
    id: 'MENU.settings.leadLossReasons',
    defaultMessage: 'Lead Loss Reasons',
  },
  '/life-health-products': {
    id: 'MENU.settings.lifeHealthProducts',
    defaultMessage: 'Life & Health Products',
  },
  '/manage-custom-fields': {
    id: 'MENU.settings.customFields',
    defaultMessage: 'Manage Custom Fields',
  },
  '/policy-type-setup': {id: 'MENU.settings.policyTypeSetup', defaultMessage: 'Policy Type Setup'},
  '/revenue-tTracking-setup': {
    id: 'MENU.settings.revenueTracking',
    defaultMessage: 'Revenue Tracking Setup',
  },
  '/sales-goal': {id: 'MENU.settings.salesGoal', defaultMessage: 'Sales Goal'},
  '/user-deductions': {
    id: 'MENU.settings.userDeductionTypes',
    defaultMessage: 'User Deductions Types',
  },
  '/my-agency': {
    id: 'MENU.settings.allAboutMyAgency',
    defaultMessage: 'All About my agency',
  },
  '/organizations': {id: 'admin.organizations.my.title', defaultMessage: 'My Organization'},
  '/members': {id: 'MENU.settings.allMembers', defaultMessage: 'All Members'},
  '/members?open=true': {
    id: 'MENU.settings.addTeamMember',
    defaultMessage: 'Add team member',
  },
  '/add-location': {id: 'MENU.settings.addLocation', defaultMessage: 'Add location'},
  '/my-account': {id: 'MENU.settings.myAccount', defaultMessage: 'My Account'},
  '/set-up-comp': {id: 'MENU.settings.setUpComp', defaultMessage: 'Set Up Comp'},
  '/run-comp': {id: 'MENU.settings.runComp', defaultMessage: 'Run Comp'},
  /** El fuente decía «Wiew Comp»: typo corregido al migrar (guía §17.6). */
  '/wiew-comp': {id: 'MENU.settings.viewComp', defaultMessage: 'View Comp'},
  '/thanks-cover-letter': {
    id: 'MENU.settings.thankYouLetter',
    defaultMessage: 'Thank You Cover Letter',
  },
  '/break-up-letter': {id: 'MENU.settings.breakUpLetter', defaultMessage: 'Break-Up Letter'},
}) as Labels

/** Etiqueta de cada entrada de `ROUTES_POLICYTOOLS_SETTINGS`, por `route`. */
const POLICYTOOLS_ROUTE_LABEL: Labels = defineMessages({
  '/policytools-settings/email': {
    id: 'admin.organizations.tab.emailSettings',
    defaultMessage: 'Email Settings',
  },
  '/organizations': {
    id: 'admin.organizations.list.title',
    defaultMessage: 'Organizations',
  },
  '#token-generator': {
    id: 'MENU.settings.appTokenGenerator',
    defaultMessage: 'App Token Generator',
  },
  '/system-monitor': {id: 'MENU.settings.systemMonitor', defaultMessage: 'System Monitor'},
}) as Labels

/** Títulos de sección de `ROUTES_SETTINGS`, por el texto del propio `title`. */
const SECTION_LABEL: Labels = {
  Manage: SETTINGS_MENU_MESSAGES.sectionManage,
  'My Agency': SETTINGS_MENU_MESSAGES.sectionMyAgency,
  Compensation: SETTINGS_MENU_MESSAGES.sectionCompensation,
  'Customer retention': SETTINGS_MENU_MESSAGES.sectionRetention,
}

const label = (intl: IntlShape, map: Labels, key?: string | null, fallback?: string | null) =>
  key && map[key] ? intl.formatMessage(map[key]) : fallback ?? key ?? ''

/** Nombre traducido de una entrada de **My Agency**. */
export const settingsRouteLabel = (
  intl: IntlShape,
  route?: string | null,
  fallback?: string | null
) => label(intl, ROUTE_LABEL, route, fallback)

/** Nombre traducido de una entrada de **Policytools Settings**. */
export const policytoolsRouteLabel = (
  intl: IntlShape,
  route?: string | null,
  fallback?: string | null
) => label(intl, POLICYTOOLS_ROUTE_LABEL, route, fallback)

/** Título traducido de una sección de **My Agency**. */
export const settingsSectionLabel = (intl: IntlShape, title?: string | null) =>
  label(intl, SECTION_LABEL, title, title)

/** Prefijo `+` de las entradas de alta. Decoración, no texto. */
export const PLUS_PREFIX = '+ '
